'use client'

/**
 * Query Options Factory for USASpending data
 *
 * Provides queryOptions objects for use with useQuery, useSuspenseQuery, and prefetchQuery.
 * Following TkDodo's recommended patterns from TanStack Query v5.
 *
 * Each entity type has a factory hook that returns:
 * - queryOptions: For use in useQuery/useSuspenseQuery
 * - prefetch: For hover/focus prefetching
 * - seedFromList: For seeding detail cache from list data
 */
import { useMemo } from 'react'

import { queryOptions, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import {
  type ApiAgencyListItem,
  type ApiAwardListItem,
  type ApiRecipientListItem,
  fetchAgency,
  fetchAgencyAnalyticsNaics,
  fetchAgencyAnalyticsRecipients,
  fetchAgencyBasicInfo,
  fetchAgencyOpportunities,
  fetchAgencyProfile,
  fetchAward,
  fetchAwardSubawards,
  fetchAwardTransactions,
  fetchPerson,
  fetchRecipient,
  fetchRecipientAnalyticsAgencies,
  fetchRecipientAnalyticsNaics,
  fetchRecipientAnalyticsPsc,
  fetchRecipientBasicInfo,
  fetchRecipientHierarchy,
  fetchRecipientInsights,
  fetchRecipientOfficers,
  fetchRecipientProfile,
  fetchRecipientRecentAwards,
  fetchRecipientStats,
  fetchSubaward,
} from '@/context/usaspending'

import type {
  AgencyProfile,
  Award,
  Buyer,
  Recipient,
  RecipientAwardSummary,
  RecipientProfile,
  Solicitation,
  SubAwardDetail,
} from '@/types/usaspending'

import { usaspendingKeys } from './queryKeys'
import { assertToken, toAwardCategory } from './utils'

/**
 * Default stale time for all queries (7 days).
 * Matches ISR revalidation period so dehydrated data is never refetched
 * during the ISR window. USASpending federal spending data updates at most
 * daily with significant reporting delays, so 7-day staleness is fine.
 */
const DEFAULT_STALE_TIME = 7 * 24 * 60 * 60 * 1000

/**
 * Default cache time (7 days - matches staleTime / ISR revalidation)
 */
const DEFAULT_GC_TIME = 7 * 24 * 60 * 60 * 1000

function normalizeRecipientAnalyticsFilters(fiscalYear?: number) {
  return {
    fiscalYear,
  }
}

// =============================================================================
// Award Query Options
// =============================================================================

/**
 * Hook providing all award-related query utilities
 */
export function useAwardQueries() {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      /**
       * Query options for award detail
       */
      detail: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.awardDetail(id),
          queryFn: () => fetchAward(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Query options for award transactions (paginated)
       */
      transactions: (awardId: string, page: number = 1, limit: number = 25) =>
        queryOptions({
          queryKey: usaspendingKeys.awardTransactions(awardId, page),
          queryFn: () =>
            fetchAwardTransactions(
              assertToken(accessToken),
              awardId,
              page,
              limit,
            ),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Query options for award subawards (paginated)
       */
      subawards: (awardId: string, page: number = 1, limit: number = 25) =>
        queryOptions({
          queryKey: usaspendingKeys.awardSubawards(awardId, page),
          queryFn: () =>
            fetchAwardSubawards(assertToken(accessToken), awardId, page, limit),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Prefetch award detail (call on hover/focus)
       */
      prefetch: (id: string) => {
        if (!accessToken) return
        return queryClient.prefetchQuery({
          queryKey: usaspendingKeys.awardDetail(id),
          queryFn: () => fetchAward(accessToken, id),
          staleTime: DEFAULT_STALE_TIME,
        })
      },

      /**
       * Prefetch award transactions (first page - for tab hover)
       */
      prefetchTransactions: (awardId: string, limit: number = 25) => {
        if (!accessToken) return
        return queryClient.prefetchQuery({
          queryKey: usaspendingKeys.awardTransactions(awardId, 1),
          queryFn: () => fetchAwardTransactions(accessToken, awardId, 1, limit),
          staleTime: DEFAULT_STALE_TIME,
        })
      },

      /**
       * Prefetch award subawards (first page - for tab hover)
       */
      prefetchSubawards: (awardId: string, limit: number = 25) => {
        if (!accessToken) return
        return queryClient.prefetchQuery({
          queryKey: usaspendingKeys.awardSubawards(awardId, 1),
          queryFn: () => fetchAwardSubawards(accessToken, awardId, 1, limit),
          staleTime: DEFAULT_STALE_TIME,
        })
      },

      /**
       * Seed preview cache from list item data.
       */
      seedFromList: (item: ApiAwardListItem) => {
        queryClient.setQueryData(
          usaspendingKeys.awardPreview(item.generated_unique_award_id),
          {
            id: String(item.award_id),
            generatedUniqueAwardId: item.generated_unique_award_id,
            title: item.description || 'Award',
            description: item.description || '',
            category: toAwardCategory(item.category),
            type: item.type,
            typeDescription: item.type_description,
            awardingAgency: {
              id: '',
              name: item.awarding_toptier_agency_name || '',
              subTier: '',
              office: '',
            },
            recipient: {
              id: item.recipient_hash || '',
              name: item.recipient_name || '',
              uei: '',
              location: { city: '', state: '', zip: '', country: '' },
              businessTypes: [],
            },
            financials: {
              obligatedAmount: item.award_amount || 0,
              currentAwardAmount: item.award_amount || 0,
              potentialAwardAmount: 0,
              nonFederalFunding: 0,
              totalOutlayedAmount: 0,
            },
            dates: {
              startDate: '',
              endDate: '',
              potentialEndDate: '',
              lastModifiedDate: '',
              dateSigned: '',
              actionDate: item.action_date || '',
            },
          } satisfies Partial<Award>,
        )
      },

      /**
       * Seed multiple award previews from list data (batch operation)
       */
      seedManyFromList: (items: ApiAwardListItem[]) => {
        items.forEach((item) => {
          queryClient.setQueryData(
            usaspendingKeys.awardPreview(item.generated_unique_award_id),
            {
              id: String(item.award_id),
              generatedUniqueAwardId: item.generated_unique_award_id,
              title: item.description || 'Award',
              description: item.description || '',
              category: toAwardCategory(item.category),
              type: item.type,
              typeDescription: item.type_description,
              awardingAgency: {
                id: '',
                name: item.awarding_toptier_agency_name || '',
                subTier: '',
                office: '',
              },
              recipient: {
                id: item.recipient_hash || '',
                name: item.recipient_name || '',
                uei: '',
                location: { city: '', state: '', zip: '', country: '' },
                businessTypes: [],
              },
              financials: {
                obligatedAmount: item.award_amount || 0,
                currentAwardAmount: item.award_amount || 0,
                potentialAwardAmount: 0,
                nonFederalFunding: 0,
                totalOutlayedAmount: 0,
              },
              dates: {
                startDate: '',
                endDate: '',
                potentialEndDate: '',
                lastModifiedDate: '',
                dateSigned: '',
                actionDate: item.action_date || '',
              },
            } satisfies Partial<Award>,
          )
        })
      },

      /**
       * Get preview data for skeleton rendering
       */
      getPreview: (id: string) =>
        queryClient.getQueryData<Partial<Award>>(
          usaspendingKeys.awardPreview(id),
        ),

      /**
       * Seed related entity preview caches from award data.
       */
      seedRelatedPreviews: (award: Award) => {
        // Seed awarding agency
        if (award.awardingAgency?.id) {
          const agencyData = createPartialAgencyFromBuyer(award.awardingAgency)
          queryClient.setQueryData(
            usaspendingKeys.agencyPreview(award.awardingAgency.id),
            agencyData,
          )
          queryClient.setQueryData(
            usaspendingKeys.agencyProfile(award.awardingAgency.id),
            agencyData,
          )
        }

        // Seed funding agency (if different from awarding)
        if (
          award.fundingAgency?.id &&
          award.fundingAgency.id !== award.awardingAgency?.id
        ) {
          const agencyData = createPartialAgencyFromBuyer(award.fundingAgency)
          queryClient.setQueryData(
            usaspendingKeys.agencyPreview(award.fundingAgency.id),
            agencyData,
          )
          queryClient.setQueryData(
            usaspendingKeys.agencyProfile(award.fundingAgency.id),
            agencyData,
          )
        }

        // Seed recipient
        if (award.recipient?.id) {
          const recipientData = createPartialRecipientFromAward(award.recipient)
          queryClient.setQueryData(
            usaspendingKeys.recipientPreview(award.recipient.id),
            recipientData,
          )
          queryClient.setQueryData(
            usaspendingKeys.recipientProfile(award.recipient.id),
            recipientData,
          )
        }
      },
    }),
    [accessToken, queryClient],
  )
}

// =============================================================================
// Recipient Query Options
// =============================================================================

/**
 * Hook providing all recipient-related query utilities
 */
export function useRecipientQueries() {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      /**
       * Query options for recipient detail
       */
      detail: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientDetail(id),
          queryFn: () => fetchRecipient(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Prefetch recipient detail
       */
      prefetch: (id: string) => {
        if (!accessToken) return
        return queryClient.prefetchQuery({
          queryKey: usaspendingKeys.recipientDetail(id),
          queryFn: () => fetchRecipient(accessToken, id),
          staleTime: DEFAULT_STALE_TIME,
        })
      },

      /**
       * Seed preview cache from list item
       */
      seedFromList: (item: ApiRecipientListItem) => {
        queryClient.setQueryData(
          usaspendingKeys.recipientPreview(item.recipient_hash),
          {
            id: item.recipient_hash,
            name: item.recipient_name,
            uei: item.uei || '',
            location: { city: '', state: '', zip: '', country: '' },
            businessTypes: [],
            stats: {
              totalPrimeAwards: item.last_12_months_count,
              totalObligatedAmount: item.last_12_months,
              lastTransactionDate: '',
            },
            topAgencies: [],
            topPscCodes: [],
            recentAwards: [],
          } satisfies Partial<RecipientProfile>,
        )
      },

      /**
       * Get preview data for skeleton rendering
       */
      getPreview: (id: string) =>
        queryClient.getQueryData<Partial<RecipientProfile>>(
          usaspendingKeys.recipientPreview(id),
        ),

      /**
       * Seed award caches from recipient's recent awards.
       */
      seedRecentAwardsPreviews: (recentAwards: RecipientAwardSummary[]) => {
        recentAwards.forEach((award) => {
          const awardData = {
            id: award.id,
            generatedUniqueAwardId: award.id,
            piid: award.piid,
            title: award.description || award.piid,
            description: award.description || '',
            category: award.type.toLowerCase().includes('grant')
              ? ('grant' as const)
              : ('contract' as const),
            type: award.type,
            typeDescription: award.type,
            awardingAgency: {
              id: '',
              name: award.agency || '',
              subTier: '',
              office: '',
            },
            recipient: {
              id: '',
              name: '',
              uei: '',
              location: { city: '', state: '', zip: '', country: '' },
              businessTypes: [],
            },
            financials: {
              obligatedAmount: award.amount || 0,
              currentAwardAmount: award.amount || 0,
              potentialAwardAmount: 0,
              nonFederalFunding: 0,
              totalOutlayedAmount: 0,
            },
            dates: {
              startDate: '',
              endDate: '',
              potentialEndDate: '',
              lastModifiedDate: '',
              dateSigned: '',
              actionDate: award.date || '',
            },
          } satisfies Partial<Award>
          queryClient.setQueryData(
            usaspendingKeys.awardPreview(award.id),
            awardData,
          )
          queryClient.setQueryData(
            usaspendingKeys.awardDetail(award.id),
            awardData,
          )
        })
      },
    }),
    [accessToken, queryClient],
  )
}

// =============================================================================
// Agency Query Options
// =============================================================================

/**
 * Hook providing all agency-related query utilities
 */
export function useAgencyQueries() {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      /**
       * Query options for agency detail
       */
      detail: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.agencyDetail(id),
          queryFn: () => fetchAgency(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Prefetch agency detail
       */
      prefetch: (id: string) => {
        if (!accessToken) return
        return queryClient.prefetchQuery({
          queryKey: usaspendingKeys.agencyDetail(id),
          queryFn: () => fetchAgency(accessToken, id),
          staleTime: DEFAULT_STALE_TIME,
        })
      },

      /**
       * Seed preview cache from list item
       */
      seedFromList: (item: ApiAgencyListItem) => {
        queryClient.setQueryData(
          usaspendingKeys.agencyPreview(item.toptier_code),
          {
            id: item.toptier_code,
            name: item.name,
            subTier: '',
            office: '',
            logoUrl: item.icon_filename || undefined,
            website: item.website || undefined,
            description: item.mission || undefined,
            topRecipients: [],
            spendByNaics: [],
            recentOpportunities: [],
          } satisfies Partial<AgencyProfile>,
        )
      },

      /**
       * Get preview data for skeleton rendering
       */
      getPreview: (id: string) =>
        queryClient.getQueryData<Partial<AgencyProfile>>(
          usaspendingKeys.agencyPreview(id),
        ),

      /**
       * Seed award caches from agency's recent opportunities.
       */
      seedRecentOpportunitiesPreviews: (opportunities: Solicitation[]) => {
        opportunities.forEach((opp) => {
          const category = opp.source === 'Grants.gov' ? 'grant' : 'contract'
          const awardData = {
            id: opp.id,
            generatedUniqueAwardId: opp.id,
            title: opp.title,
            description: opp.description || '',
            category,
            type: category === 'grant' ? 'Grant' : 'Contract',
            typeDescription:
              category === 'grant' ? 'Grant Opportunity' : 'Contract',
            awardingAgency: {
              id: '',
              name: '',
              subTier: '',
              office: '',
            },
            recipient: {
              id: '',
              name: '',
              uei: '',
              location: { city: '', state: '', zip: '', country: '' },
              businessTypes: [],
            },
            financials: {
              obligatedAmount: 0,
              currentAwardAmount: 0,
              potentialAwardAmount: 0,
              nonFederalFunding: 0,
              totalOutlayedAmount: 0,
            },
            dates: {
              startDate: opp.postedDate || '',
              endDate: opp.responseDeadline || '',
              potentialEndDate: '',
              lastModifiedDate: '',
              dateSigned: '',
              actionDate: opp.postedDate || '',
            },
            solicitation: opp,
          } satisfies Partial<Award>
          queryClient.setQueryData(
            usaspendingKeys.awardPreview(opp.id),
            awardData,
          )
          queryClient.setQueryData(
            usaspendingKeys.awardDetail(opp.id),
            awardData,
          )
        })
      },
    }),
    [accessToken, queryClient],
  )
}

// =============================================================================
// Granular Agency Section Query Options
// =============================================================================

/**
 * Hook providing granular agency section query utilities.
 */
export function useAgencySectionQueries() {
  const { accessToken } = useAuthInfo()

  return useMemo(
    () => ({
      /**
       * Query options for agency basic info (fast, RDS-only).
       * Used by header section for immediate title/logo/description.
       */
      basicInfo: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.agencyBasicInfo(id),
          queryFn: () => fetchAgencyBasicInfo(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Query options for agency profile (basic info + stats).
       */
      profile: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.agencyProfile(id),
          queryFn: () => fetchAgencyProfile(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Query options for top recipients analytics
       */
      analyticsRecipients: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.agencyAnalyticsRecipients(id),
          queryFn: () =>
            fetchAgencyAnalyticsRecipients(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Query options for NAICS analytics
       */
      analyticsNaics: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.agencyAnalyticsNaics(id),
          queryFn: () =>
            fetchAgencyAnalyticsNaics(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Query options for recent opportunities
       */
      opportunities: (id: string, limit: number = 5) =>
        queryOptions({
          queryKey: usaspendingKeys.agencyOpportunities(id),
          queryFn: () =>
            fetchAgencyOpportunities(assertToken(accessToken), id, limit),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),
    }),
    [accessToken],
  )
}

// =============================================================================
// Granular Recipient Section Query Options
// =============================================================================

/**
 * Hook providing granular recipient section query utilities.
 *
 * Uses `accessToken ?? ''` instead of `assertToken(accessToken)` so that
 * queryFn never throws when auth is unavailable (e.g. during ISR hydration).
 * An empty token causes `createHeaders` to omit the Authorization header,
 * hitting the backend's `maybe_auth_user_id` path instead.
 */
export function useRecipientSectionQueries() {
  const { accessToken } = useAuthInfo()
  // Fallback to empty string so queryFn makes an unauthenticated request
  // instead of throwing during React 18 selective hydration on the ISR page.
  const token = accessToken ?? ''

  return useMemo(
    () => ({
      basicInfo: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientBasicInfo(id),
          queryFn: () => fetchRecipientBasicInfo(token, id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      profile: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientProfile(id),
          queryFn: () => fetchRecipientProfile(token, id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      /**
       * Query options for recipient stats (KPIs)
       */
      stats: (id: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientStats(
            id,
            normalizeRecipientAnalyticsFilters(fiscalYear),
          ),
          queryFn: () => fetchRecipientStats(token, id, fiscalYear),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      /**
       * Query options for top awarding agencies analytics
       */
      analyticsAgencies: (id: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientAnalyticsAgencies(
            id,
            normalizeRecipientAnalyticsFilters(fiscalYear),
          ),
          queryFn: () => fetchRecipientAnalyticsAgencies(token, id, fiscalYear),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      /**
       * Query options for PSC analytics
       */
      analyticsPsc: (id: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientAnalyticsPsc(
            id,
            normalizeRecipientAnalyticsFilters(fiscalYear),
          ),
          queryFn: () => fetchRecipientAnalyticsPsc(token, id, fiscalYear),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      /**
       * Query options for NAICS analytics
       */
      analyticsNaics: (id: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientAnalyticsNaics(
            id,
            normalizeRecipientAnalyticsFilters(fiscalYear),
          ),
          queryFn: () => fetchRecipientAnalyticsNaics(token, id, fiscalYear),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      /**
       * Query options for recent awards
       */
      recentAwards: (id: string, limit: number = 5) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientRecentAwards(id, limit),
          queryFn: () => fetchRecipientRecentAwards(token, id, limit),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      /**
       * Query options for recipient insights (pre-ranked by backend)
       */
      insights: (id: string, limit: number = 4, fiscalYear?: number) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientInsights(
            id,
            limit,
            normalizeRecipientAnalyticsFilters(fiscalYear),
          ),
          queryFn: () => fetchRecipientInsights(token, id, limit, fiscalYear),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      /**
       * Query options for recipient hierarchy (parent/child/sibling tree)
       */
      hierarchy: (recipientHash: string) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientHierarchy(recipientHash),
          queryFn: () => fetchRecipientHierarchy(token, recipientHash),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      officers: (id: string, enabled: boolean = false) =>
        queryOptions({
          queryKey: usaspendingKeys.recipientOfficers(id),
          queryFn: () => fetchRecipientOfficers(token, id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled,
        }),
    }),
    [token],
  )
}

// =============================================================================
// Person Query Options
// =============================================================================

/**
 * Hook providing all person-related query utilities
 */
export function usePersonQueries() {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      /**
       * Query options for person detail
       */
      detail: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.personDetail(id),
          queryFn: () => fetchPerson(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Prefetch person detail
       */
      prefetch: (id: string) => {
        if (!accessToken) return
        return queryClient.prefetchQuery({
          queryKey: usaspendingKeys.personDetail(id),
          queryFn: () => fetchPerson(accessToken, id),
          staleTime: DEFAULT_STALE_TIME,
        })
      },

      /**
       * Get cached data
       */
      getCached: (id: string) =>
        queryClient.getQueryData(usaspendingKeys.personDetail(id)),
    }),
    [accessToken, queryClient],
  )
}

// =============================================================================
// Cache Seeding Helpers
// =============================================================================

function createPartialAgencyFromBuyer(buyer: Buyer): Partial<AgencyProfile> {
  return {
    id: buyer.id,
    name: buyer.name,
    subTier: buyer.subTier,
    office: buyer.office,
    logoUrl: buyer.logoUrl,
    location: buyer.location,
    website: buyer.website,
    description: buyer.description,
  }
}

function createPartialRecipientFromAward(
  recipient: Recipient,
): Partial<RecipientProfile> {
  return {
    id: recipient.id,
    name: recipient.name,
    uei: recipient.uei,
    duns: recipient.duns,
    cageCode: recipient.cageCode,
    location: recipient.location,
    businessTypes: recipient.businessTypes,
    parent: recipient.parent,
  }
}

// =============================================================================
// Subaward Query Options
// =============================================================================

/**
 * Hook providing all subaward-related query utilities
 */
export function useSubawardQueries() {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      /**
       * Query options for subaward detail
       */
      detail: (id: string) =>
        queryOptions({
          queryKey: usaspendingKeys.subawardDetail(id),
          queryFn: () => fetchSubaward(assertToken(accessToken), id),
          staleTime: DEFAULT_STALE_TIME,
          gcTime: DEFAULT_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      /**
       * Prefetch subaward detail (call on hover/focus)
       */
      prefetch: (id: string) => {
        if (!accessToken) return
        return queryClient.prefetchQuery({
          queryKey: usaspendingKeys.subawardDetail(id),
          queryFn: () => fetchSubaward(accessToken, id),
          staleTime: DEFAULT_STALE_TIME,
        })
      },

      /**
       * Get cached subaward data
       */
      getCached: (id: string) =>
        queryClient.getQueryData<SubAwardDetail>(
          usaspendingKeys.subawardDetail(id),
        ),
    }),
    [accessToken, queryClient],
  )
}
